// External libraries
import React, { useEffect, useState } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { spinnerLiteState, spinnerState } from "../../states";
import { Heading, SpinnerLite, TypographyColor, TypographyType } from "../../ui-elements";


interface lineChartProps {
    graphData: any,
    selectedData: string,
    selectedDataIndex: number,
    setSelectedDataIndex: (selectedDataIndex: number) => void
    setSelectedData: (selectedData: string) => void,
    color: string
    headerText: string
    breakDownType: string
    selectedYear: string
    isSingleLine: boolean

}


export const LineChartComponentcatSubCat = ({ graphData, selectedData, selectedDataIndex, setSelectedDataIndex, setSelectedData, color, headerText, breakDownType, selectedYear, isSingleLine }: lineChartProps) => {
    const spinner = useRecoilValue(spinnerState);
    const [spinnerLite, setSpinnerLite] = useRecoilState(spinnerLiteState);
    const [combinedData, setCombinedData] = useState<any[]>([]);
    // Function to format date
    const formatDate = (dateString: string): string => {
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, "0"); // Ensure 2-digit day
        const month = date.toLocaleString("en-US", { month: "short" }); // Get short month name
        const year = date.getFullYear(); // Get full year

        return `${day}-${month}-${year}`;
    };

    // Transform graphData
    graphData.forEach((category: any) => {
        // Format dates in growthInTime
        category.growthInTime = category.growthInTime.map((entry: any) => ({
            ...entry,
            date: formatDate(entry.date),
        }));

        // Format dates in average
        category.average = category.average.map((entry: any) => ({
            ...entry,
            date: formatDate(entry.date),
        }));
    });


    const handleLineClick = (name: string) => {
        setSelectedData(name);
    }


    useEffect(() => {
        const newCombinedData: any[] = [];
        graphData.forEach((category: any) => {
            // Process `growthInTime`
            category.growthInTime.forEach((entry: any) => {
                const existing = newCombinedData.find((d) => d.date === entry.date);
                if (existing) {
                    existing[category.name] = entry.value;
                    existing[`${category.name}_avg`] = entry.averageValue;
                    existing[`${category.name}_desc`] = entry.description;
                    existing[`${category.name}_date`] = entry.date;
                } else {
                    newCombinedData.push({
                        date: entry.date,
                        [category.name]: entry.value,
                        [`${category.name}_avg`]: entry.averageValue,
                        [`${category.name}_desc`]: entry.description,
                        [`${category.name}_date`]: entry.date,
                    });
                }
            });

            // Process `average` if present
            if (category.average) {
                category.average.forEach((entry: any) => {
                    const existing = newCombinedData.find((d) => d.date === entry.date);
                    if (existing) {
                        existing.Average = entry.value;
                        existing.Average_avg = entry.averageValue;
                        existing.Average_desc = entry.description;
                        existing.Average_date = entry.date;
                    } else {
                        newCombinedData.push({
                            date: entry.date,
                            Average: entry.value,
                            Average_avg: entry.averageValue,
                            Average_desc: entry.description,
                            Average_date: entry.date,
                        });
                    }
                });
            }
        });
        newCombinedData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        // Update the state with the new combined data
        setCombinedData(newCombinedData);
    }, [graphData])

    const CustomTooltip = ({ active, payload }: any) => {
        if (active && payload && payload.length) {
            let data;
            const selectedPayload = payload.find((entry: any) => entry.name === selectedData);

            if (selectedPayload) {
                data = selectedPayload.payload;
            } else {
                console.warn("No matching data found for selectedData:", selectedData);
            }

            if (data) {
                return (
                    <div
                        style={{
                            position: "relative",
                            backgroundColor: "#fff",
                            borderRadius: "8px",
                            padding: "10px",
                            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                            fontSize: "14px",
                            fontFamily: "Arial, sans-serif",
                        }}
                    >
                        <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
                            <p style={{ margin: 0 }} className="tooltipLinechartDate">{data[`${selectedData}_date`] || "N/A"}</p>
                        </div>
                        <hr style={{ margin: 0 }} />
                        <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
                            <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: color }} className="me-1"></div>
                            <h6 style={{ margin: 0, color: "#000", }} className="me-1 margin-left-1 tooltipLinechartDate">
                                {selectedPayload?.name || "N/A"} PoI:
                            </h6>
                            <p style={{ margin: 0, fontWeight: "bold" }} className="tooltipLinechartDate">
                                {selectedPayload?.value?.toLocaleString() || "0"}
                            </p>
                        </div>
                        <p
                            style={{ margin: 0, display: 'flex', flexDirection: 'row', alignItems: 'center', fontSize: '0.5785vw', color: '#3C3C4399', fontWeight: '500', fontFamily: "Poppins" }}
                            className="ms-1"
                        >
                            {/* {headerText === 'Sub Category' &&
                                data[`${selectedData}_desc`]?.includes("lesser") ? (
                                <IoIosArrowDown style={{ color: "#CA1A0A" }} className="me-2" />
                            ) : (
                                <IoIosArrowUp style={{ color: "#0DA62E" }} className="me-1" />
                            )} */}
                            {data[`${selectedData}_desc`] || "No description available"}
                        </p>
                        <hr style={{ margin: 0 }} />
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                            {isSingleLine && <><div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: " #4D4D4D " }} className="me-1 margin-left-1"></div>
                                <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: " #4D4D4D " }} className="me-1"></div></>}

                            <p style={{ margin: 0, }} className="p-1 tooltipLinechartDate"> {breakDownType + " Average"}
                                :</p>
                            <p style={{ margin: 0, fontWeight: "bold" }} className="p-1 tooltipLinechartDate">
                                {data[`${selectedData}_avg`]?.toLocaleString() || "N/A"}
                            </p>
                        </div>
                    </div>
                );
            }
        }

        return null;
    };

    // Custom Tooltip Component
    const NationalLevelCustomTooltip = ({ active, payload }: any) => {

        if (active && payload && payload.length) {
            var data;

            if (payload.length > selectedDataIndex) {
                data = payload[selectedDataIndex]?.payload;
            }
            else {
                data = payload[0].payload;
            }

            return (
                <div
                    style={{
                        position: "relative",
                        backgroundColor: "#fff",
                        borderRadius: "8px",
                        padding: "10px",
                        boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                        fontSize: "14px",
                        fontFamily: "Arial, sans-serif",
                    }}
                >
                    <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
                        {/* <MdOutlineDateRange className="me-1" /> */}
                        <p style={{ margin: 0 }} className="tooltipLinechartDate">{data[`${(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex]?.name : payload[0]?.name}_date`]}</p>
                    </div>
                    <hr style={{ margin: 0 }} />
                    <div style={{ display: "flex", flexDirection: 'row', alignItems: 'center' }} className="p-1">
                        <div style={{ height: '0.306vh', width: '0.926vw', backgroundColor: color }} className="me-1"></div>

                        <h6 style={{ margin: 0, color: "#000" }} className="me-1 tooltipLinechartDate">{(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex]?.name : payload[0]?.name} PoI:</h6>
                        <p style={{ margin: 0, fontWeight: "bold" }} className="tooltipLinechartDate">
                            {(payload.length > selectedDataIndex && selectedDataIndex !== -1) ? payload[selectedDataIndex]?.value?.toLocaleString() : payload[0]?.value?.toLocaleString()}{" "}

                        </p>

                    </div>

                </div>
            );
        }
        return null;
    };

    // Custom legend with modified names and icons
    const CustomLegend = (props: any) => {
        const { payload } = props;
        let otherSubcategoryRendered = false; // Track if "Other Subcategory Pols" has been rendered

        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {Array.isArray(payload) &&
                    payload
                        .filter((entry: any) => {
                            // Filter logic to render "Other Subcategory Pols" only once
                            if (
                                entry.dataKey !== selectedData &&
                                entry.dataKey !== 'average' &&
                                !otherSubcategoryRendered
                            ) {
                                otherSubcategoryRendered = true;
                                return true; // Allow rendering for the first unmatched entry
                            }
                            return entry.dataKey.toString().trim() === selectedData.toString().trim() || entry.dataKey === 'average';
                        })
                        .map((entry: any, index: number) => (
                            <div
                                key={index}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    marginRight: '10px',
                                }}
                            >
                                {/* Icon */}
                                {(breakDownType === "" && entry.dataKey === 'Average') ? <></> : entry.dataKey === 'Average' ? (
                                    // Two small horizontal lines for "Average" icon
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            marginRight: '5px',
                                        }}

                                    >
                                        <div
                                            style={{
                                                width: '10px',
                                                height: '3px',
                                                backgroundColor: '#787486',
                                            }}
                                            className="me-1"
                                        ></div>
                                        <div
                                            style={{
                                                width: '10px',
                                                height: '3px',
                                                backgroundColor: '#787486',
                                            }}
                                        ></div>
                                    </div>
                                ) : (
                                    <span
                                        style={{
                                            display: 'inline-block',
                                            width: '0.7vw',
                                            height: '0.51vh',
                                            backgroundColor: entry.dataKey.toString().trim() === selectedData.toString().trim() ? color : entry.dataKey === 'Average' ? '#787486' : '#DBDBDB',
                                            marginRight: '0.33vw',

                                        }}
                                    ></span>
                                )}

                                {isSingleLine ? (
                                    // <span>
                                    //     {entry.dataKey.toString().trim() === selectedData.toString().trim()
                                    //         ? selectedData
                                    //         : entry.dataKey === 'Average' && breakDownType === 'District' ? 'Nation Average'
                                    //             : entry.dataKey === 'Average' && breakDownType === 'Sub District' ? 'State Average'
                                    //                 : breakDownType === 'State' ? '' : 'District Average'}
                                    // </span>
                                    <Heading
                                        title={entry.dataKey.toString().trim() === selectedData.toString().trim()
                                            ? selectedData
                                            : entry.dataKey === 'Average' && breakDownType===''? breakDownType:  breakDownType + ' Average'}
                                        type={TypographyType.h5}
                                        colour={TypographyColor.secondary}
                                        classname='text-start'
                                    />
                                ) : (
                                    <Heading
                                        title={
                                            entry.dataKey.toString().trim() === selectedData.toString().trim() ? selectedData
                                                : entry.dataKey === 'Average' && breakDownType === 'District' ? 'Nation Average'
                                                    : entry.dataKey === 'Average' && breakDownType === 'Sub District' ? 'State Average'
                                                        : entry.dataKey === 'Average' && breakDownType === 'State' ? '' : `Other ${headerText} Pols`
                                        }
                                        type={TypographyType.h5}
                                        colour={TypographyColor.secondary}
                                        classname='text-start'
                                    />
                                    // <span>
                                    //     {
                                    //         entry.dataKey.toString().trim() === selectedData.toString().trim() ? selectedData
                                    //             : entry.dataKey === 'Average' && breakDownType === 'District' ? 'Nation Average'
                                    //                 : entry.dataKey === 'Average' && breakDownType === 'Sub District' ? 'State Average'
                                    //                     : entry.dataKey === 'Average' && breakDownType === 'State' ? '' : `Other ${headerText} Pols`
                                    //     }
                                    // </span>
                                )}

                            </div>
                        ))}
            </div>
        );
    };

    // Get unique years for X-axis
    const uniqueYears = Array.from(
        new Set(combinedData.map(item => new Date(item.date).getFullYear()))
    ).sort((a, b) => b - a); // Ensure years are sorted



    // Formatter function for Y-axis ticks
    const formatYAxis = (value: number): string => {
        if (value >= 1_00_00_000) { // Greater than or equal to 1 crore
            return `${Math.floor((value / 1_00_00_000) * 2) / 2} Cr`; // Round down to the nearest 0.5 and format as Crores
        } else if (value >= 1_00_000) { // Greater than or equal to 1 lac
            return `${Math.floor((value / 1_00_000) * 2) / 2} L`; // Round down to the nearest 0.5 and format as Lacs
        } else if (value >= 1_000) { // Greater than or equal to 1 thousand
            return `${Math.floor((value / 1_000) * 2) / 2} K`; // Round down to the nearest 0.5 and format as Thousands
        } else {
            return `${value}`; // Format as is for small values
        }
    };

    useEffect(() => {
        // Update the selectedDataIndex based on selectedData change
        const index = graphData.findIndex((category: any) => category.name === selectedData);
        if (index !== -1) {
            setSelectedDataIndex(index);
        }
        // else {
        //     setSelectedDataIndex(0); // Default to the first index
        // }
        setSelectedDataIndex(index);
    }, [selectedData, graphData]);

    const calinterval = combinedData.length > 0 ? Math.floor(combinedData.length / 10) : 1; // Set a minimum interval of 1 for small arrays

    useEffect(() => {
        if (combinedData.length > 0) {
            setSpinnerLite(prev => prev - 1);
        }
    }, [combinedData]);

    const getYAxisWidth = (data: any[]) => {
        if (!data || data.length === 0) return 40; // Default width if no data
        const maxValue = Math.max(
            ...data.flatMap((d) =>
                Object.values(d)
                    .map((v) => (typeof v === "number" ? v : 0)) // Extract numeric values
            )
        );

        return maxValue > 9999999 ? 100 : maxValue > 99999 ? 80 : 70; // Adjust width dynamically
    };

    return (
        (spinner.spinners.length === 0 && spinnerLite > 0 && combinedData.length === 0 ? <div className='w-100' style={{ height: '100%' }}>
            <SpinnerLite />
        </div> :
            <ResponsiveContainer width='100%' height={350}>
                <LineChart data={combinedData} >
                    <CartesianGrid horizontal={false} vertical={false} />
                    {selectedYear === '1' ? (
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            fontSize='0.81vw'
                            style={{ fontWeight: 600 }}
                            padding={{ left: 20, right: 0 }}
                            tickMargin={5}
                            interval={calinterval}
                        />
                    ) : (
                        <XAxis
                            dataKey="date"
                            type="category" // For categorical data
                            tickLine={false}
                            axisLine={false}
                            fontSize='0.81vw'
                            style={{ fontWeight: 600, }}
                            tickMargin={5}
                            tickFormatter={(value: string) => new Date(value).getFullYear().toString()} // Format date to display the year
                            ticks={uniqueYears.map((year) => {
                                // Find the first instance of each year in the dataset
                                const match = combinedData.find((item: any) => new Date(item.date).getFullYear() === Number(year));
                                return match ? match.date : null; // Return the matching date for the year
                            }).filter(Boolean)} // Filter out null or undefined values
                        />

                    )}

                    <YAxis tickLine={false} // Optionally hide tick lines too
                        fontSize='0.81vw'
                        style={{ fontWeight: 600, }}
                        axisLine={false} // Hide the X-axis line
                        // tickMargin={10}
                        interval={0}
                        padding={{ top: 10, bottom: 10 }}
                        width={getYAxisWidth(combinedData)}
                    />
                    <Tooltip content={breakDownType === '' ? NationalLevelCustomTooltip : CustomTooltip} cursor={true} />
                    <Legend content={<CustomLegend />} wrapperStyle={{ paddingTop: '1rem' }} iconType={'square'} />
                    {isSingleLine && graphData.length > 0 && combinedData.length > 0 ?
                        (
                            graphData.map((category: any, index: number) => (
                                selectedData === category.name && (
                                    <React.Fragment key={`fragment-${category.name}-${index}`}>
                                        <Line
                                            key={`${combinedData[index]?.date}-${index}`} // Unique key for Line component
                                            type="monotone"
                                            dataKey={category.name}
                                            stroke={color} // Color of the selected line
                                            strokeWidth={3} // Thicker line for selected data
                                            connectNulls={true}
                                            dot={(props) => (
                                                <circle
                                                    key={`circlelocation1-${props.cx}`}
                                                    cx={props.cx}
                                                    cy={props.cy}
                                                    r={6} // Larger dot for selected line
                                                    fill={color} // Color of the dot
                                                    style={{
                                                        visibility: props.isHovered ? 'visible' : 'hidden', // Show only on hover
                                                    }}
                                                />
                                            )}
                                            onClick={() => handleLineClick(category.name)} // Select line when clicked
                                        />
                                        <Line
                                            key={`averageLocation-${combinedData[index + 1]?.value}`} // Unique key for average line
                                            type="monotone"
                                            dataKey="Average"
                                            stroke="#787486"
                                            strokeWidth={2}
                                            strokeDasharray="5 5" // Creates a dashed line
                                            connectNulls={true}
                                            dot={(props) => (
                                                <circle
                                                    key={`circle2location-${props.cx}`}
                                                    cx={props.cx}
                                                    cy={props.cy}
                                                    r={6}
                                                    fill={color}
                                                    style={{
                                                        visibility: props.isHovered ? 'visible' : 'hidden',
                                                    }}
                                                />
                                            )}
                                        />
                                    </React.Fragment>
                                )
                            ))
                        )

                        : graphData.length > 0 ? (
                            graphData.map((category: any, index: number) => (
                                <Line
                                    key={`${combinedData[index]?.date}-${index}-line`}
                                    type="monotone"
                                    dataKey={category.name}
                                    stroke={category.name.toString().trim() === selectedData.toString().trim() ? color : "#DBDBDB"}
                                    strokeWidth={category.name.toString().trim() === selectedData.toString().trim() ? 5 : 1}
                                    connectNulls={true}
                                    onClick={() => handleLineClick(category.name)}
                                    style={{ cursor: 'pointer' }}
                                    dot={(props) => {
                                        // Show dot only on hover
                                        return (
                                            <circle
                                                key={`locationcircle3-${props.cx}`}
                                                cx={props.cx}
                                                cy={props.cy}
                                                r={6}
                                                fill={color}
                                                style={{
                                                    visibility: props.isHovered && category.name.toString().trim() === selectedData.toString().trim() ? 'visible' : 'hidden',
                                                }}
                                            />
                                        );
                                    }}
                                />))
                        )
                            : (
                                <p>No data</p>
                            )}
                </LineChart>
            </ResponsiveContainer>
        )
    );
};
